import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { useLabels } from '@/contexts/WorkspaceContext';
import {
  AREA_UNITS, AreaUnit, CreateUnitInput, FURNISHINGS, Furnishing, PropertyType, PropertyUnit, RENT_PERIODS, RentPeriod,
  UNIT_PURPOSES, UNIT_STATUSES, UNIT_TYPES, UnitPurpose, UnitStatus, UnitType, UpdateUnitInput,
} from '@/services/real-estate.service';
import {
  AREA_UNIT_LABEL, ContactPicker, FURNISHING_LABEL, PickedContact, PURPOSE_LABEL, RENT_PERIOD_LABEL, UNIT_STATUS_LABEL, UNIT_TYPE_LABEL,
  withArticle,
} from './shared';

/** Form state for one unit — inputs stay strings until they're sent. */
export interface UnitDraft {
  label: string;
  reference: string;
  unitType: UnitType;
  purpose: UnitPurpose;
  status: UnitStatus;
  bedrooms: string;
  bathrooms: string;
  area: string;
  areaUnit: AreaUnit;
  floor: string;
  furnishing: Furnishing | '';
  parkingSpaces: string;
  rentPrice: string;
  rentPeriod: RentPeriod;
  salePrice: string;
  availableFrom: string;
  /** The unit's own owner; null = the property's owner. */
  owner: PickedContact | null;
  amenities: string;
  imageUrls: string;
  description: string;
  notes: string;
}

export type UnitErrors = Partial<Record<keyof UnitDraft, string>>;

const UNIT_TYPE_FOR: Partial<Record<PropertyType, UnitType>> = {
  villa: 'villa', townhouse: 'townhouse', office_building: 'office', retail: 'shop', warehouse: 'warehouse', land: 'plot',
};
/** What a new unit usually is inside this kind of property. */
export const defaultUnitType = (t: PropertyType): UnitType => UNIT_TYPE_FOR[t] ?? 'apartment';

export const emptyUnitDraft = (patch: Partial<UnitDraft> = {}): UnitDraft => ({
  label: '', reference: '', unitType: 'apartment', purpose: 'rent', status: 'available',
  bedrooms: '', bathrooms: '', area: '', areaUnit: 'sqft', floor: '', furnishing: '', parkingSpaces: '',
  rentPrice: '', rentPeriod: 'yearly', salePrice: '', availableFrom: '', owner: null,
  amenities: '', imageUrls: '', description: '', notes: '',
  ...patch,
});

const numText = (v?: number | null) => (v === null || v === undefined ? '' : String(v));

/**
 * The API reports the EFFECTIVE owner on a unit (its own, else the property's),
 * so an owner equal to the property's is treated as inherited.
 */
export function draftFromUnit(u: PropertyUnit, propertyOwnerUid?: string): UnitDraft {
  const ownOwner = u.ownerContactUid && u.ownerContactUid !== propertyOwnerUid
    ? { contactUid: u.ownerContactUid, name: u.ownerName || 'Owner' }
    : null;
  return {
    label: u.label,
    reference: u.reference ?? '',
    unitType: u.unitType,
    purpose: u.purpose,
    status: u.status,
    bedrooms: numText(u.bedrooms),
    bathrooms: numText(u.bathrooms),
    area: numText(u.area),
    areaUnit: (AREA_UNITS as readonly string[]).includes(u.areaUnit) ? (u.areaUnit as AreaUnit) : 'sqft',
    floor: numText(u.floor),
    furnishing: u.furnishing ?? '',
    parkingSpaces: numText(u.parkingSpaces),
    rentPrice: numText(u.rentPrice),
    rentPeriod: u.rentPeriod ?? 'yearly',
    salePrice: numText(u.salePrice),
    availableFrom: u.availableFrom ? u.availableFrom.slice(0, 10) : '',
    owner: ownOwner,
    amenities: (u.amenities ?? []).join(', '),
    imageUrls: (u.imageUrls ?? []).join('\n'),
    description: u.description ?? '',
    notes: u.notes ?? '',
  };
}

/** null = empty, NaN = not a number. */
const parseNum = (s: string): number | null => {
  const t = s.trim().replace(/,/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

export const splitList = (s: string) => Array.from(new Set(s.split(/[,\n]/).map((x) => x.trim()).filter(Boolean)));
// URLs can carry commas (ImageKit transforms), so links split on whitespace only.
export const splitLinks = (s: string) => Array.from(new Set(s.split(/\s+/).map((x) => x.trim()).filter(Boolean)));

export function isWebLink(s: string): boolean {
  try {
    const u = new URL(s);
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.includes('.');
  } catch {
    return false;
  }
}

/** Mirrors the backend's UnitFieldsDto limits so a save doesn't bounce off ValidationPipe. */
export function validateUnitDraft(d: UnitDraft): UnitErrors {
  const e: UnitErrors = {};
  if (!d.label.trim()) e.label = 'Give the unit a label, e.g. “Apt 1204”';
  const check = (k: keyof UnitDraft, v: string, opts: { int?: boolean; min: number; max?: number }) => {
    const n = parseNum(v);
    if (n === null) return;
    const bad = Number.isNaN(n) || n < opts.min || (opts.max !== undefined && n > opts.max) || (opts.int && !Number.isInteger(n));
    if (bad) {
      const range = opts.max !== undefined ? ` from ${opts.min} to ${opts.max}` : opts.min === 0 ? ' of 0 or more' : '';
      e[k] = `Enter a ${opts.int ? 'whole number' : 'number'}${range}`;
    }
  };
  check('bedrooms', d.bedrooms, { int: true, min: 0, max: 50 });
  check('bathrooms', d.bathrooms, { min: 0, max: 50 });
  check('area', d.area, { min: 0 });
  check('floor', d.floor, { int: true, min: -5, max: 300 });
  check('parkingSpaces', d.parkingSpaces, { int: true, min: 0, max: 50 });
  if (d.purpose !== 'sale') check('rentPrice', d.rentPrice, { min: 0 });
  if (d.purpose !== 'rent') check('salePrice', d.salePrice, { min: 0 });
  const links = splitLinks(d.imageUrls);
  const badLink = links.find((l) => !isWebLink(l));
  if (badLink) e.imageUrls = `Not a web link: ${badLink.slice(0, 60)}`;
  else if (links.length > 20) e.imageUrls = 'Up to 20 image links';
  if (splitList(d.amenities).length > 40) e.amenities = 'Up to 40 amenities';
  return e;
}

const numOrUndef = (s: string) => {
  const n = parseNum(s);
  return n === null || Number.isNaN(n) ? undefined : n;
};

/** New unit → create body. Empty fields are left out; prices hidden by the purpose aren't sent. */
export function unitCreatePayload(d: UnitDraft): CreateUnitInput {
  return {
    label: d.label.trim(),
    reference: d.reference.trim() || undefined,
    unitType: d.unitType,
    purpose: d.purpose,
    status: d.status,
    bedrooms: numOrUndef(d.bedrooms),
    bathrooms: numOrUndef(d.bathrooms),
    area: numOrUndef(d.area),
    areaUnit: d.areaUnit,
    floor: numOrUndef(d.floor),
    furnishing: d.furnishing || undefined,
    parkingSpaces: numOrUndef(d.parkingSpaces),
    rentPrice: d.purpose !== 'sale' ? numOrUndef(d.rentPrice) : undefined,
    rentPeriod: d.rentPeriod,
    salePrice: d.purpose !== 'rent' ? numOrUndef(d.salePrice) : undefined,
    availableFrom: d.availableFrom || undefined,
    ownerContactUid: d.owner?.contactUid || undefined,
    amenities: splitList(d.amenities),
    imageUrls: splitLinks(d.imageUrls),
    description: d.description.trim() || undefined,
    notes: d.notes.trim() || undefined,
  };
}

/** Edited unit → only what changed. Cleared numbers, dates and choices go as null; cleared text as ''. */
export function unitPatch(d: UnitDraft, was: UnitDraft): UpdateUnitInput {
  const p: UpdateUnitInput = {};
  const text = (k: 'label' | 'reference' | 'description' | 'notes') => d[k].trim() !== was[k].trim();
  if (text('label')) p.label = d.label.trim();
  if (text('reference')) p.reference = d.reference.trim();
  if (text('description')) p.description = d.description.trim();
  if (text('notes')) p.notes = d.notes.trim();
  if (d.unitType !== was.unitType) p.unitType = d.unitType;
  if (d.purpose !== was.purpose) p.purpose = d.purpose;
  if (d.status !== was.status) p.status = d.status;
  if (d.areaUnit !== was.areaUnit) p.areaUnit = d.areaUnit;
  if (d.rentPeriod !== was.rentPeriod) p.rentPeriod = d.rentPeriod;
  const num = (k: 'bedrooms' | 'bathrooms' | 'area' | 'floor' | 'parkingSpaces' | 'rentPrice' | 'salePrice') => {
    if (d[k].trim() === was[k].trim()) return;
    const n = parseNum(d[k]);
    p[k] = n === null || Number.isNaN(n) ? null : n;
  };
  num('bedrooms'); num('bathrooms'); num('area'); num('floor'); num('parkingSpaces'); num('rentPrice'); num('salePrice');
  if (d.furnishing !== was.furnishing) p.furnishing = d.furnishing || null;
  if (d.availableFrom !== was.availableFrom) p.availableFrom = d.availableFrom || null;
  if ((d.owner?.contactUid ?? '') !== (was.owner?.contactUid ?? '')) p.ownerContactUid = d.owner?.contactUid ?? '';
  if (splitList(d.amenities).join('\n') !== splitList(was.amenities).join('\n')) p.amenities = splitList(d.amenities);
  if (splitLinks(d.imageUrls).join('\n') !== splitLinks(was.imageUrls).join('\n')) p.imageUrls = splitLinks(d.imageUrls);
  return p;
}

/** One-line recap for a collapsed unit card. */
export function draftRecap(d: UnitDraft): string {
  const beds = d.bedrooms.trim() === '0' ? 'Studio' : d.bedrooms.trim() ? `${d.bedrooms.trim()} bed` : '';
  return [UNIT_TYPE_LABEL[d.unitType], beds, PURPOSE_LABEL[d.purpose]].filter(Boolean).join(' · ');
}

interface Props {
  draft: UnitDraft;
  onChange: (patch: Partial<UnitDraft>) => void;
  errors: UnitErrors;
  currency: string;
  /** The unit has an active lease — its status follows the lease. */
  leaseLocked?: boolean;
  /** Tuck the less-used fields behind "More details" (the multi-unit property form). */
  compact?: boolean;
  autoFocus?: boolean;
}

export function UnitFields({ draft: d, onChange, errors, currency, leaseLocked, compact, autoFocus }: Props) {
  const t = useLabels();
  const hasMoreValues = !!(d.area || d.floor || d.furnishing || d.availableFrom || d.owner || d.amenities || d.imageUrls || d.description || d.notes || d.reference);
  const [more, setMore] = useState(!compact || hasMoreValues);
  const beds = Number(d.bedrooms);
  const bedOptions = Array.from({ length: 11 }, (_, i) => i);
  if (Number.isInteger(beds) && beds > 10) bedOptions.push(beds);
  const money = <span className="text-2xs font-semibold">{currency}</span>;

  return (
    <div>
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
        <FormField label="Label" required error={errors.label}>
          <Input value={d.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="e.g. Apt 1204, Villa 12" maxLength={80} invalid={!!errors.label} autoFocus={autoFocus} />
        </FormField>
        <FormField label="Type">
          <Select value={d.unitType} onChange={(e) => onChange({ unitType: e.target.value as UnitType })}>
            {UNIT_TYPES.map((u) => <option key={u} value={u}>{UNIT_TYPE_LABEL[u]}</option>)}
          </Select>
        </FormField>
      </div>

      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
        <FormField label="Listed">
          <Select value={d.purpose} onChange={(e) => onChange({ purpose: e.target.value as UnitPurpose })}>
            {UNIT_PURPOSES.map((p) => <option key={p} value={p}>{PURPOSE_LABEL[p]}</option>)}
          </Select>
        </FormField>
        <FormField label="Status" hint={leaseLocked ? 'Follows the active lease' : undefined}>
          <Select value={d.status} onChange={(e) => onChange({ status: e.target.value as UnitStatus })} disabled={leaseLocked}>
            {UNIT_STATUSES.map((s) => (
              <option key={s} value={s} disabled={s === 'rented' && d.status !== 'rented'}>
                {UNIT_STATUS_LABEL[s]}{s === 'rented' && d.status !== 'rented' ? ' (add a lease)' : ''}
              </option>
            ))}
          </Select>
        </FormField>
      </div>

      <div className="grid grid-cols-3 gap-x-3">
        <FormField label="Bedrooms" error={errors.bedrooms}>
          <Select value={d.bedrooms} onChange={(e) => onChange({ bedrooms: e.target.value })}>
            <option value="">—</option>
            {bedOptions.map((n) => <option key={n} value={String(n)}>{n === 0 ? 'Studio' : n}</option>)}
          </Select>
        </FormField>
        <FormField label="Bathrooms" error={errors.bathrooms}>
          <Input type="number" inputMode="decimal" min={0} max={50} step={0.5} value={d.bathrooms} onChange={(e) => onChange({ bathrooms: e.target.value })} invalid={!!errors.bathrooms} />
        </FormField>
        <FormField label="Parking" error={errors.parkingSpaces}>
          <Input type="number" inputMode="numeric" min={0} max={50} step={1} value={d.parkingSpaces} onChange={(e) => onChange({ parkingSpaces: e.target.value })} invalid={!!errors.parkingSpaces} />
        </FormField>
      </div>

      {d.purpose !== 'sale' && (
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
          <FormField label="Rent" error={errors.rentPrice}>
            <Input type="number" inputMode="decimal" min={0} step="any" value={d.rentPrice} onChange={(e) => onChange({ rentPrice: e.target.value })} placeholder="0" iconLeft={money} invalid={!!errors.rentPrice} />
          </FormField>
          <FormField label="Period">
            <Select value={d.rentPeriod} onChange={(e) => onChange({ rentPeriod: e.target.value as RentPeriod })} className="h-10">
              {RENT_PERIODS.map((p) => <option key={p} value={p}>{RENT_PERIOD_LABEL[p]}</option>)}
            </Select>
          </FormField>
        </div>
      )}
      {d.purpose !== 'rent' && (
        <FormField label="Sale price" error={errors.salePrice}>
          <Input type="number" inputMode="decimal" min={0} step="any" value={d.salePrice} onChange={(e) => onChange({ salePrice: e.target.value })} placeholder="0" iconLeft={money} invalid={!!errors.salePrice} />
        </FormField>
      )}

      {compact && (
        <button
          type="button"
          onClick={() => setMore((m) => !m)}
          aria-expanded={more}
          className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          <ChevronDown size={14} className={clsx('transition-transform', more && 'rotate-180')} />
          {more ? 'Fewer details' : 'More details — size, floor, furnishing, owner, photos'}
        </button>
      )}

      {more && (
        <div>
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <FormField label="Reference" hint="Your listing or permit number">
              <Input value={d.reference} onChange={(e) => onChange({ reference: e.target.value })} maxLength={80} />
            </FormField>
            <FormField label="Available from">
              <Input type="date" value={d.availableFrom} onChange={(e) => onChange({ availableFrom: e.target.value })} />
            </FormField>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-x-3">
            <FormField label="Size" error={errors.area}>
              <Input type="number" inputMode="decimal" min={0} step="any" value={d.area} onChange={(e) => onChange({ area: e.target.value })} invalid={!!errors.area} />
            </FormField>
            <FormField label="Unit">
              <Select value={d.areaUnit} onChange={(e) => onChange({ areaUnit: e.target.value as AreaUnit })} className="h-10">
                {AREA_UNITS.map((a) => <option key={a} value={a}>{AREA_UNIT_LABEL[a]}</option>)}
              </Select>
            </FormField>
            <FormField label="Floor" error={errors.floor}>
              <Input type="number" inputMode="numeric" min={-5} max={300} step={1} value={d.floor} onChange={(e) => onChange({ floor: e.target.value })} placeholder="0 = ground" invalid={!!errors.floor} />
            </FormField>
          </div>
          <FormField label="Furnishing">
            <Select value={d.furnishing} onChange={(e) => onChange({ furnishing: e.target.value as Furnishing | '' })}>
              <option value="">—</option>
              {FURNISHINGS.map((f) => <option key={f} value={f}>{FURNISHING_LABEL[f]}</option>)}
            </Select>
          </FormField>
          <FormField label="Owner" hint={`Leave empty to use the property’s owner`}>
            <ContactPicker value={d.owner} onChange={(owner) => onChange({ owner })} placeholder={`Find the owner (${withArticle(t('contacts', 'singular').toLowerCase())})`} />
          </FormField>
          <FormField label="Amenities" hint="Separate with commas" error={errors.amenities}>
            <Input value={d.amenities} onChange={(e) => onChange({ amenities: e.target.value })} placeholder="Balcony, Sea view, Maid's room" />
          </FormField>
          <FormField label="Photo links" hint="One link per line" error={errors.imageUrls}>
            <Textarea rows={2} value={d.imageUrls} onChange={(e) => onChange({ imageUrls: e.target.value })} placeholder="https://…" />
          </FormField>
          <FormField label="Description" hint="What you’d put in the listing">
            <Textarea rows={3} maxLength={4000} value={d.description} onChange={(e) => onChange({ description: e.target.value })} />
          </FormField>
          <FormField label="Internal notes" hint="Only your team sees these">
            <Textarea rows={2} maxLength={4000} value={d.notes} onChange={(e) => onChange({ notes: e.target.value })} />
          </FormField>
        </div>
      )}
    </div>
  );
}
